/**
 * OIDC Authentication Token Handler
 * Handles token capture, storage, and validation for Firebase Identity Platform
 *
 * @author Shopify Theme Developer
 * @version 1.0.0
 */

// PRIORITY: Handle OIDC flow redirect IMMEDIATELY if we're on auth-callback page
(function() {
  // Only run on auth-callback page
  if (!window.location.pathname.includes('/pages/auth-callback')) {
    return;
  }

  const urlParams = new URLSearchParams(window.location.search);
  const shopifyRedirectUri = urlParams.get('shopify_redirect_uri');
  const authCode = urlParams.get('code');
  const state = urlParams.get('state');
  const isPopup = window.opener && !window.opener.closed;

  // OIDC flow: has shopify_redirect_uri - redirect to Shopify (works in both popup and non-popup)
  if (shopifyRedirectUri && authCode && state) {
    // Validate redirect URL to prevent open redirect attacks
    const ALLOWED_REDIRECT_DOMAINS = [
      'myshopify.com',
      'shopify.com',
      'accounts.shopify.com',
      'mimeophotos.com'
    ];

    try {
      const parsed = new URL(shopifyRedirectUri);
      const hostname = parsed.hostname.toLowerCase();
      const isAllowed = ALLOWED_REDIRECT_DOMAINS.some(domain =>
        hostname === domain || hostname.endsWith('.' + domain)
      );

      if (!isAllowed) {
        return;
      }
    } catch (e) {
      return;
    }

    // Extract Firebase tokens from hash fragment AND query params
    // Auth-app may send tokens as:
    //   hash: #firebase_token=xxx&firebase_refresh_token=yyy
    //   query: ?idToken=xxx&refreshToken=yyy (camelCase from auth-app OIDC flow)
    const hash = window.location.hash.substring(1); // Remove leading #
    let firebaseToken = null;
    let firebaseRefreshToken = null;
    if (hash) {
      const hashParams = new URLSearchParams(hash);
      firebaseToken = hashParams.get('firebase_token');
      firebaseRefreshToken = hashParams.get('firebase_refresh_token');
    }

    // Fallback: check query params for camelCase aliases (idToken, refreshToken)
    if (!firebaseToken) {
      firebaseToken = urlParams.get('idToken') || urlParams.get('firebase_token');
    }
    if (!firebaseRefreshToken) {
      firebaseRefreshToken = urlParams.get('refreshToken') || urlParams.get('firebase_refresh_token');
    }

    // Build Shopify callback URL
    const shopifyCallback = new URL(shopifyRedirectUri);
    shopifyCallback.searchParams.set('code', authCode);
    shopifyCallback.searchParams.set('state', state);
    const redirectUrl = shopifyCallback.toString();

    // If inside Cordova InAppBrowser, send tokens back to main WebView via postMessage.
    // InAppBrowser has isolated localStorage/cookies — storing here would be lost on close.
    if (window.webkit && window.webkit.messageHandlers && window.webkit.messageHandlers.cordova_iab) {
      window.webkit.messageHandlers.cordova_iab.postMessage(JSON.stringify({
        type: 'oidc_complete',
        firebaseToken: firebaseToken,
        firebaseRefreshToken: firebaseRefreshToken,
        shopifyCallbackUrl: redirectUrl,
        isSuccess: true
      }));
      // Stop — main WebView will store tokens and complete the OIDC redirect
      return;
    }

    // Not in InAppBrowser — store tokens locally and redirect (web browser flow)
    if (firebaseToken) {
      try { localStorage.setItem('auth_token', firebaseToken); } catch (e) {}
    }
    if (firebaseRefreshToken) {
      try { localStorage.setItem('refresh_token', firebaseRefreshToken); } catch (e) {}
    }
    // Set auth_timestamp so isTokenExpired() doesn't treat the token as expired
    if (firebaseToken || firebaseRefreshToken) {
      try { localStorage.setItem('auth_timestamp', Date.now().toString()); } catch (e) {}
    }

    // Redirect to complete Shopify OIDC flow
    window.location.href = redirectUrl;

    // Prevent rest of script from running
    throw new Error('OIDC redirect in progress - stopping script execution');
  }

  // Popup without recognizable auth flow - just close
  if (isPopup) {
    window.close();
    return;
  }
})();

(function() {
  'use strict';

  // Configuration
  const CONFIG = {
    TOKEN_EXPIRY_HOURS: 1,
    // Background refresh settings
    TOKEN_REFRESH_BUFFER_MINUTES: 5, // Refresh token when this many minutes before expiry
    TOKEN_CHECK_INTERVAL_MS: 60000,  // Check token status every 1 minute
    STORAGE_KEYS: {
      ACCESS_TOKEN: 'auth_token',
      REFRESH_TOKEN: 'refresh_token',
      TOKEN_TIMESTAMP: 'auth_timestamp',
      REDIRECT_URL: 'auth_redirect_url',
      // Note: Email is NOT stored separately - it's parsed from auth_token when needed (security)
      USER_AVATAR: 'auth_user_avatar',
      USER_NAME: 'auth_user_name',
      USER_GIVEN_NAME: 'auth_user_given_name',
      USER_FAMILY_NAME: 'auth_user_family_name'
    },
    EVENTS: {
      AUTH_COMPLETE: 'authenticationComplete',
      AUTH_ERROR: 'authenticationError',
      AUTH_LOGOUT: 'authenticationLogout',
      TOKEN_REFRESHED: 'tokenRefreshed',
      TOKEN_REFRESH_FAILED: 'tokenRefreshFailed'
    },
    DEFAULT_REDIRECT: '/account',
    // Allowed redirect domains for OIDC flow (prevents open redirect attacks)
    ALLOWED_REDIRECT_DOMAINS: [
      'myshopify.com',
      'shopify.com',
      'accounts.shopify.com',
      'mimeophotos.com'
    ]
  };

  /**
   * Validate that a redirect URL is safe (prevents open redirect attacks)
   * @param {string} url - The URL to validate
   * @returns {boolean} - True if URL is safe to redirect to
   */
  function isValidRedirectUrl(url) {
    try {
      const parsed = new URL(url);
      const hostname = parsed.hostname.toLowerCase();

      // Check if hostname matches allowed domains
      return CONFIG.ALLOWED_REDIRECT_DOMAINS.some(domain => {
        return hostname === domain || hostname.endsWith('.' + domain);
      });
    } catch (e) {
      // Invalid URL
      return false;
    }
  }

  /**
   * Validate that a URL is safe for internal redirect (same-origin or relative path)
   * Prevents open redirect attacks via localStorage manipulation
   * @param {string} url - The URL to validate
   * @returns {boolean} - True if URL is safe for internal redirect
   */
  function isValidInternalUrl(url) {
    if (!url || typeof url !== 'string') return false;

    // Block dangerous protocols (javascript, data, vbscript)
    const lowerUrl = url.toLowerCase().trim();
    if (lowerUrl.startsWith('javascript:') || lowerUrl.startsWith('data:') || lowerUrl.startsWith('vbscript:')) {
      return false;
    }

    // Allow relative URLs (start with / but not //)
    if (url.startsWith('/') && !url.startsWith('//')) {
      return true;
    }

    // For absolute URLs, verify same origin
    try {
      const parsed = new URL(url);
      return parsed.origin === window.location.origin;
    } catch (e) {
      return false;
    }
  }

  /**
   * Storage Helper with fallback and error handling
   */
  const Storage = {
    isAvailable: false,

    // Check if localStorage is available
    checkAvailability() {
      try {
        const test = '__storage_test__';
        localStorage.setItem(test, test);
        localStorage.removeItem(test);
        this.isAvailable = true;
        return true;
      } catch (e) {
        this.isAvailable = false;
        return false;
      }
    },

    // Set item in localStorage
    /**
     * @param {string} key
     * @param {string} value
     * @returns {boolean}
     */
    set(key, value) {
      if (!this.isAvailable && !this.checkAvailability()) {
        return false;
      }

      try {
        localStorage.setItem(key, value);
        return true;
      } catch (e) {
        return false;
      }
    },

    // Get item from localStorage
    /**
     * @param {string} key
     * @returns {string | null}
     */
    get(key) {
      if (!this.isAvailable && !this.checkAvailability()) {
        return null;
      }

      try {
        return localStorage.getItem(key);
      } catch (e) {
        return null;
      }
    },

    // Remove item from localStorage
    /**
     * @param {string} key
     * @returns {boolean}
     */
    remove(key) {
      if (!this.isAvailable && !this.checkAvailability()) {
        return false;
      }

      try {
        localStorage.removeItem(key);
        return true;
      } catch (e) {
        return false;
      }
    },

    // Clear all auth-related data
    clearAuth() {
      Object.values(CONFIG.STORAGE_KEYS).forEach(key => {
        this.remove(key);
      });
      // Clean up legacy key (email used to be stored separately)
      this.remove('auth_user_email');
    }
  };

  /**
   * Token Parser - Extracts tokens from URL
   */
  const TokenParser = {
    /**
     * Parse tokens from URL (both query params and hash fragments)
     * Prioritizes hash fragments for security
     * @returns {{
     *   access_token: string | null,
     *   refresh_token: string | null,
     *   id_token: string | null,
     *   expires_in: string | null,
     *   token_type: string | null,
     *   error: string | null,
     *   error_description: string | null
     * }}
     */
    parseFromURL() {
      const tokens = {
        access_token: null,
        refresh_token: null,
        id_token: null,
        expires_in: null,
        token_type: null,
        error: null,
        error_description: null
      };

      try {
        // Parse hash fragment first (more secure, not logged by servers)
        const hash = window.location.hash.substring(1);
        if (hash) {
          const hashParams = new URLSearchParams(hash);
          this._extractTokens(hashParams, tokens);
        }

        // Parse query parameters as fallback
        const searchParams = new URLSearchParams(window.location.search);
        if (searchParams.toString()) {
          this._extractTokens(searchParams, tokens);
        }

        return tokens;
      } catch (e) {
        return {
          access_token: null,
          refresh_token: null,
          id_token: null,
          expires_in: null,
          token_type: null,
          error: 'parse_error',
          error_description: 'Failed to parse authentication response'
        };
      }
    },

    /**
     * Extract token values from URLSearchParams
     * @param {URLSearchParams} params
     * @param {{
     *   access_token: string | null,
     *   refresh_token: string | null,
     *   id_token: string | null,
     *   expires_in: string | null,
     *   token_type: string | null,
     *   error: string | null,
     *   error_description: string | null
     * }} tokens
     */
    _extractTokens(params, tokens) {
      // Extract all possible token parameters
      const tokenKeys = /** @type {const} */ ([
        'access_token', 'refresh_token', 'id_token',
        'expires_in', 'token_type', 'error', 'error_description'
      ]);

      tokenKeys.forEach(key => {
        const value = params.get(key);
        if (value) {
          // @ts-ignore - Dynamic key assignment
          if (!tokens[key]) {
            tokens[key] = value;
          }
        }
      });

      // Handle firebase_token/firebase_refresh_token aliases (from auth app)
      const firebaseToken = params.get('firebase_token');
      const firebaseRefreshToken = params.get('firebase_refresh_token');
      if (firebaseToken && !tokens.access_token) {
        tokens.access_token = firebaseToken;
      }
      if (firebaseRefreshToken && !tokens.refresh_token) {
        tokens.refresh_token = firebaseRefreshToken;
      }

      // Handle camelCase aliases from auth-app OIDC flow (idToken, refreshToken)
      const idToken = params.get('idToken');
      const camelRefreshToken = params.get('refreshToken');
      if (idToken && !tokens.access_token) {
        tokens.access_token = idToken;
      }
      if (camelRefreshToken && !tokens.refresh_token) {
        tokens.refresh_token = camelRefreshToken;
      }
    },

    /**
     * Clean URL by removing tokens
     */
    cleanURL() {
      try {
        // Get clean path without query or hash
        const cleanPath = window.location.pathname;

        // Replace state without reload
        window.history.replaceState(
          {},
          document.title,
          cleanPath
        );

        return true;
      } catch (e) {
        return false;
      }
    }
  };

  /**
   * Token Manager - Handles token storage and validation
   */
  const TokenManager = {
    /**
     * Store authentication tokens
     * @param {{
     *   access_token?: string | null,
     *   refresh_token?: string | null,
     *   id_token?: string | null
     * }} tokens
     * @returns {boolean}
     */
    storeTokens(tokens) {
      try {
        const timestamp = Date.now();

        // Store access token (required)
        if (tokens.access_token) {
          Storage.set(CONFIG.STORAGE_KEYS.ACCESS_TOKEN, tokens.access_token);
        }

        // Store refresh token (optional)
        if (tokens.refresh_token) {
          Storage.set(CONFIG.STORAGE_KEYS.REFRESH_TOKEN, tokens.refresh_token);
        }

        // Store ID token (optional, contains user claims)
        if (tokens.id_token) {
          try {
            const claims = this._parseJWT(tokens.id_token);
            this._storeUserClaims(claims);
          } catch (e) {
            // Could not parse ID token
          }
        }

        // Also try to extract user claims from access_token (Firebase ID token may come as access_token)
        if (tokens.access_token && !Storage.get(CONFIG.STORAGE_KEYS.USER_AVATAR)) {
          try {
            const claims = this._parseJWT(tokens.access_token);
            this._storeUserClaims(claims);
          } catch (e) {
            // access_token is not a JWT or doesn't contain user claims
          }
        }

        // Store timestamp
        Storage.set(CONFIG.STORAGE_KEYS.TOKEN_TIMESTAMP, timestamp.toString());

        // Clear MediaClip's anonymous session marker
        // This prevents MediaClip from showing the "save to account" dialog
        Storage.remove('anonymousSessionId');

        return true;
      } catch (e) {
        return false;
      }
    },

    /**
     * Parse JWT token to extract claims
     * @param {string} token
     * @returns {Record<string, any>}
     */
    _parseJWT(token) {
      try {
        const base64Url = token.split('.')[1];
        if (!base64Url) {
          throw new Error('Invalid JWT format');
        }
        const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
        const jsonPayload = decodeURIComponent(
          atob(base64).split('').map(c => {
            return '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2);
          }).join('')
        );
        return JSON.parse(jsonPayload);
      } catch (e) {
        return {};
      }
    },

    /**
     * Store user claims from JWT to localStorage
     * @param {Record<string, any>} claims
     */
    _storeUserClaims(claims) {
      // Note: Email is NOT stored separately for security - parse from token when needed
      // Store avatar URL from OAuth provider (Google, Apple, etc.)
      if (claims.picture) {
        Storage.set(CONFIG.STORAGE_KEYS.USER_AVATAR, claims.picture);
      }
      // Store user name information for initials fallback
      if (claims.name) {
        Storage.set(CONFIG.STORAGE_KEYS.USER_NAME, claims.name);
      }
      if (claims.given_name) {
        Storage.set(CONFIG.STORAGE_KEYS.USER_GIVEN_NAME, claims.given_name);
      }
      if (claims.family_name) {
        Storage.set(CONFIG.STORAGE_KEYS.USER_FAMILY_NAME, claims.family_name);
      }
    },

    /**
     * Check if token is expired
     */
    isTokenExpired() {
      const timestamp = Storage.get(CONFIG.STORAGE_KEYS.TOKEN_TIMESTAMP);

      if (!timestamp) {
        return true;
      }

      const tokenAge = Date.now() - parseInt(timestamp, 10);
      const expiryTime = CONFIG.TOKEN_EXPIRY_HOURS * 60 * 60 * 1000;

      return tokenAge > expiryTime;
    },

    /**
     * Check if token is expired by reading the 'exp' claim from JWT
     * More accurate than timestamp-based check
     * @returns {boolean} - True if token is expired or invalid
     */
    isTokenExpiredFromJWT() {
      const token = Storage.get(CONFIG.STORAGE_KEYS.ACCESS_TOKEN);
      if (!token) {
        return true;
      }

      try {
        const claims = this._parseJWT(token);
        if (!claims.exp) {
          // No exp claim, fall back to timestamp check
          return this.isTokenExpired();
        }

        // exp is in seconds, Date.now() is in milliseconds
        const expiryTime = claims.exp * 1000;
        return Date.now() >= expiryTime;
      } catch (e) {
        // Error parsing token, assume expired
        return true;
      }
    },

    /**
     * Get time until token expires in milliseconds (from JWT exp claim)
     * @returns {number} - Milliseconds until expiry, or 0 if expired/invalid
     */
    getTimeUntilExpiryFromJWT() {
      const token = Storage.get(CONFIG.STORAGE_KEYS.ACCESS_TOKEN);
      if (!token) {
        return 0;
      }

      try {
        const claims = this._parseJWT(token);
        if (!claims.exp) {
          return 0;
        }

        // exp is in seconds, Date.now() is in milliseconds
        const expiryTime = claims.exp * 1000;
        const timeUntilExpiry = expiryTime - Date.now();
        return Math.max(0, timeUntilExpiry);
      } catch (e) {
        return 0;
      }
    },

    /**
     * Get current access token if valid
     */
    getAccessToken() {
      if (this.isTokenExpired()) {
        return null;
      }

      return Storage.get(CONFIG.STORAGE_KEYS.ACCESS_TOKEN);
    },

    /**
     * Get refresh token
     */
    getRefreshToken() {
      return Storage.get(CONFIG.STORAGE_KEYS.REFRESH_TOKEN);
    },

    /**
     * Validate token against Shopify customer session
     */
    validateWithShopify() {
      const hasToken = !!this.getAccessToken();
      const shopifyLoggedIn = window.shopifyCustomer?.isLoggedIn || false;
      const shopifyCustomerId = window.shopifyCustomer?.customerId || null;
      const shopifyEmail = window.shopifyCustomer?.email || null;

      // Both should match for valid state
      if (hasToken !== shopifyLoggedIn) {
        return false;
      }

      return true;
    }
  };

  /**
   * UI Controller - Manages callback page UI states
   */
  const UIController = {
    showLoading() {
      this._setVisibility('auth-loading', true);
      this._setVisibility('auth-error', false);
      this._setVisibility('auth-success', false);
    },

    /**
     * @param {string} [message]
     */
    showSuccess(message) {
      this._setVisibility('auth-loading', false);
      this._setVisibility('auth-error', false);
      this._setVisibility('auth-success', true);

      const successMsg = document.getElementById('auth-success-message');
      if (successMsg && message) {
        successMsg.textContent = message;
      }
    },

    /**
     * @param {string} [message]
     */
    showError(message) {
      this._setVisibility('auth-loading', false);
      this._setVisibility('auth-error', true);
      this._setVisibility('auth-success', false);

      const errorMsg = document.getElementById('auth-error-message');
      if (errorMsg) {
        errorMsg.textContent = message || 'An unexpected error occurred during authentication.';
      }
    },

    /**
     * @param {string} elementId
     * @param {boolean} visible
     */
    _setVisibility(elementId, visible) {
      const element = document.getElementById(elementId);
      if (element) {
        element.style.display = visible ? 'block' : 'none';
      }
    }
  };

  /**
   * Redirect Manager - Handles post-auth navigation
   */
  const RedirectManager = {
    /**
     * Save intended destination before login
     * @param {string} url
     * @returns {boolean}
     */
    saveRedirectURL(url) {
      try {
        sessionStorage.setItem(CONFIG.STORAGE_KEYS.REDIRECT_URL, url);
        return true;
      } catch (e) {
        return false;
      }
    },

    /**
     * Get and clear saved redirect URL
     */
    getRedirectURL() {
      try {
        const url = sessionStorage.getItem(CONFIG.STORAGE_KEYS.REDIRECT_URL);
        sessionStorage.removeItem(CONFIG.STORAGE_KEYS.REDIRECT_URL);
        return url || CONFIG.DEFAULT_REDIRECT;
      } catch (e) {
        return CONFIG.DEFAULT_REDIRECT;
      }
    },

    /**
     * Perform redirect after authentication
     */
    redirect(delay = 2000) {
      setTimeout(() => {
        const destination = this.getRedirectURL();
        window.location.href = destination;
      }, delay);
    }
  };

  /**
   * Main Authentication Handler
   */
  const AuthHandler = {
    /**
     * Initialize authentication callback handling
     */
    init() {
      // Initialize storage
      Storage.checkAvailability();

      // Show loading state
      UIController.showLoading();

      // Parse tokens from URL
      const tokens = TokenParser.parseFromURL();

      // Check for errors
      if (tokens.error) {
        this.handleError(tokens);
        return;
      }

      // Validate we have required tokens
      if (!tokens.access_token) {
        this.handleError({
          error: 'missing_token',
          error_description: 'No access token received from authentication provider'
        });
        return;
      }

      // Store tokens
      const stored = TokenManager.storeTokens(tokens);
      if (!stored) {
        this.handleError({
          error: 'storage_error',
          error_description: 'Failed to store authentication tokens'
        });
        return;
      }

      // Clean URL
      TokenParser.cleanURL();

      // Validate with Shopify
      TokenManager.validateWithShopify();

      // Dispatch success event
      this.dispatchAuthEvent(CONFIG.EVENTS.AUTH_COMPLETE, {
        timestamp: Date.now(),
        hasRefreshToken: !!tokens.refresh_token
      });

      // Check if we need to continue OIDC flow to Shopify
      const urlParams = new URLSearchParams(window.location.search);
      const shopifyRedirectUri = urlParams.get('shopify_redirect_uri');
      const authCode = urlParams.get('code');
      const state = urlParams.get('state');

      // Check if we're in a popup (opened by parent window for auth)
      const isPopup = window.opener && window.opener !== window;

      if (shopifyRedirectUri && authCode && state) {
        // Validate redirect URL to prevent open redirect attacks
        if (!isValidRedirectUrl(shopifyRedirectUri)) {
          this.handleError({
            error: 'invalid_redirect',
            error_description: 'The redirect URL is not allowed'
          });
          return;
        }

        // Continue OIDC flow - redirect to Shopify with auth code
        UIController.showSuccess('Authentication successful! Completing login...');
        const shopifyCallback = new URL(shopifyRedirectUri);
        shopifyCallback.searchParams.set('code', authCode);
        shopifyCallback.searchParams.set('state', state);

        setTimeout(() => {
          window.location.href = shopifyCallback.toString();
        }, 1000);
      } else if (isPopup) {
        // We're in a popup - tokens are already stored in localStorage
        // Just close the popup, parent will detect and handle redirect
        UIController.showSuccess('Authentication successful! Closing...');
        setTimeout(() => {
          window.close();
        }, 500);
      } else {
        // Check for pending customize URL first (set by theme.liquid before login)
        const pendingCustomizeUrl = localStorage.getItem('autoCustomize_url');
        if (pendingCustomizeUrl && isValidInternalUrl(pendingCustomizeUrl)) {
          // Only clear the URL - keep autoCustomize flag for handleAutoCustomize() on product page
          localStorage.removeItem('autoCustomize_url');
          // NOTE: Do NOT clear 'autoCustomize' or 'autoCustomize_timestamp' here!
          // Those flags are read by handleAutoCustomize() in theme.liquid after redirect
          UIController.showSuccess('Authentication successful! Opening designer...');

          // Redirect to customize URL - handleAutoCustomize() will auto-click the button
          setTimeout(() => {
            window.location.href = pendingCustomizeUrl;
          }, 1000);
        } else {
          // Normal redirect flow
          UIController.showSuccess('Authentication successful! Redirecting...');
          RedirectManager.redirect(2000);
        }
      }
    },

    /**
     * Handle authentication errors
     * @param {{
     *   error?: string | null,
     *   error_description?: string | null
     * }} tokens
     */
    handleError(tokens) {
      const errorMessage = tokens.error_description ||
                          `Authentication failed: ${tokens.error}`;

      UIController.showError(errorMessage);

      // Dispatch error event
      this.dispatchAuthEvent(CONFIG.EVENTS.AUTH_ERROR, {
        error: tokens.error,
        description: tokens.error_description
      });
    },

    /**
     * Dispatch custom authentication event
     * @param {string} eventName
     * @param {any} detail
     */
    dispatchAuthEvent(eventName, detail) {
      try {
        const event = new CustomEvent(eventName, {
          detail: detail,
          bubbles: true,
          cancelable: false
        });
        window.dispatchEvent(event);
      } catch (e) {
        // Error dispatching event
      }
    }
  };

  var logoutCartClearInFlight = null;
  var LOGOUT_CART_CLEAR_PENDING_KEY = 'authState_logout_cart_clear_pending_v1';

  function setLogoutCartClearPending(pending) {
    try {
      if (pending) {
        sessionStorage.setItem(LOGOUT_CART_CLEAR_PENDING_KEY, '1');
      } else {
        sessionStorage.removeItem(LOGOUT_CART_CLEAR_PENDING_KEY);
      }
    } catch (e) {}
  }

  // Caps the deferred clear so a permanently failing endpoint cannot re-clear
  // a signed-in shopper's cart on every page load.
  var DEFERRED_LOGOUT_CLEAR_ATTEMPTS_KEY = 'authState_logout_cart_clear_attempts_v1';
  var DEFERRED_LOGOUT_CLEAR_MAX_ATTEMPTS = 3;

  function readDeferredLogoutClearAttempts() {
    try {
      return Number(sessionStorage.getItem(DEFERRED_LOGOUT_CLEAR_ATTEMPTS_KEY) || 0) || 0;
    } catch (e) {
      return 0;
    }
  }

  function recordDeferredLogoutClearAttempt() {
    try {
      sessionStorage.setItem(
        DEFERRED_LOGOUT_CLEAR_ATTEMPTS_KEY,
        String(readDeferredLogoutClearAttempts() + 1)
      );
    } catch (e) {}
  }

  function resetDeferredLogoutClearAttempts() {
    try {
      sessionStorage.removeItem(DEFERRED_LOGOUT_CLEAR_ATTEMPTS_KEY);
    } catch (e) {}
  }

  function hasExhaustedDeferredLogoutClearAttempts() {
    return readDeferredLogoutClearAttempts() >= DEFERRED_LOGOUT_CLEAR_MAX_ATTEMPTS;
  }

  function hasLogoutCartClearPending() {
    try {
      return sessionStorage.getItem(LOGOUT_CART_CLEAR_PENDING_KEY) === '1';
    } catch (e) {
      return false;
    }
  }

  // Paired with the pre-paint badge suppression in layout/theme.liquid.
  function releaseLogoutBadgeSuppression() {
    try {
      document.documentElement.removeAttribute('data-logout-clear-pending');
    } catch (e) {}
  }

  function clearMediaclipClientCartState() {
    try {
      sessionStorage.removeItem('projectDialog_pendingProjectName');
      sessionStorage.removeItem('mimeo_cart_source');
      sessionStorage.removeItem('country_restore_attempted');
      sessionStorage.removeItem('__mimeo_needs_cart_normalize');
    } catch (e) {}

    try {
      localStorage.removeItem('autoCustomize');
      localStorage.removeItem('autoCustomize_url');
      localStorage.removeItem('autoCustomize_timestamp');
    } catch (e) {}

    try {
      document.cookie = 'newest_cart_item_keys=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/; SameSite=Lax; Secure';
    } catch (e) {}
  }

  // Distinguishes "the response body was not JSON" from "the body parsed to
  // null"; the two used to be indistinguishable and the former was treated as
  // a successful clear.
  var CART_CLEAR_PARSE_FAILED = {};

  function postCartClear() {
    var endpoints = buildCartEndpoints('/cart/clear.js');

    function attempt(index) {
      if (index >= endpoints.length) {
        return Promise.reject(new Error('Cart clear failed on all endpoints'));
      }

      return fetch(endpoints[index], {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'same-origin',
        keepalive: true
      })
        .then(function(response) {
          if (!response || !response.ok) {
            return null;
          }

          if (typeof response.json !== 'function') {
            return { cart: null };
          }

          return response.json()
            .catch(function() {
              return CART_CLEAR_PARSE_FAILED;
            })
            .then(function(cart) {
              // A localized storefront answers an unprefixed cart POST with a
              // redirect that downgrades to GET, so a 200 can still carry a
              // full cart — or an HTML body that is not JSON at all. Both mean
              // nothing was cleared. Collapsing the parse failure to `null`
              // used to fall through to the success branch below, which
              // declared the clear done and skipped the locale-prefixed
              // endpoint that would actually have worked.
              if (cart === CART_CLEAR_PARSE_FAILED) {
                return null;
              }
              if (cart && typeof cart.item_count === 'number' && cart.item_count > 0) {
                return null;
              }
              return { cart: cart };
            });
        })
        .catch(function() {
          return null;
        })
        .then(function(result) {
          // One retry decision point, so an exhausted chain cannot re-issue a
          // request that already failed.
          if (!result) return attempt(index + 1);
          return result.cart;
        });
    }

    return attempt(0);
  }

  function runSerializedCartMutation(mutationCallback) {
    if (typeof window.__mimeoRunCartMutation === 'function') {
      return window.__mimeoRunCartMutation(mutationCallback);
    }
    return Promise.resolve().then(mutationCallback);
  }

  function clearShopifyCartForLogout() {
    clearMediaclipClientCartState();

    if (logoutCartClearInFlight && typeof logoutCartClearInFlight.then === 'function') {
      return logoutCartClearInFlight;
    }

    // Survives the Shopify logout redirect chain so the landing page can finish
    // the clear if navigation outran it. Referrer is unreliable across domains.
    setLogoutCartClearPending(true);

    // Queue behind in-flight cart writes (add-to-cart, MediaClip sync) so a
    // late-landing mutation cannot repopulate the cart after it is cleared.
    logoutCartClearInFlight = runSerializedCartMutation(function() {
      // Attributes survive /cart/clear.js, so clear the promo first and let the
      // line-item clear be the last write to commit.
      return clearPromoCodeCartAttribute()
        .catch(function() {})
        .then(function() {
          return postCartClear();
        });
    })
      .finally(function() {
        logoutCartClearInFlight = null;
      });

    return logoutCartClearInFlight;
  }

  function removeCartCountBubble() {
    try {
      var cartIconBubble = document.getElementById('cart-icon-bubble');
      if (!cartIconBubble) {
        return;
      }

      var bubble = cartIconBubble.querySelector('.cart-count-bubble');
      if (bubble) {
        bubble.remove();
      }
    } catch (e) {}
  }

  function refreshCartIconBubbleAfterLogout() {
    var cartIconBubble = document.getElementById('cart-icon-bubble');
    if (!cartIconBubble) {
      return Promise.resolve();
    }

    var cartUrl = (typeof routes !== 'undefined' && routes && routes.cart_url) ? routes.cart_url : '/cart';

    return fetch(cartUrl + '?section_id=cart-icon-bubble', {
      credentials: 'same-origin',
      cache: 'no-store'
    })
      .then(function(response) {
        if (!response.ok) {
          throw new Error('Failed to refresh cart icon bubble with status ' + response.status);
        }
        return response.text();
      })
      .then(function(html) {
        var parsed = new DOMParser().parseFromString(html, 'text/html');
        var nextBubbleMarkup = parsed.getElementById('cart-icon-bubble');

        if (nextBubbleMarkup) {
          cartIconBubble.innerHTML = nextBubbleMarkup.innerHTML;
          return;
        }

        var sectionContent = parsed.querySelector('.shopify-section');
        if (sectionContent) {
          cartIconBubble.innerHTML = sectionContent.innerHTML;
          return;
        }

        removeCartCountBubble();
      })
      .catch(function() {
        removeCartCountBubble();
      });
  }

  /**
   * Global Authentication State Object
   * Accessible via window.authState throughout the theme
   */
  let refreshPromise = null; // Debounce: track in-flight refresh request
  let authRedirectInFlight = false;
  let authRedirectResetTimer = null;

  function normalizeAuthFailureReason(reason) {
    const normalizedReason = String(reason || 'session-expired');
    if (normalizedReason === 'missing-token' || normalizedReason.endsWith('missing-token') || normalizedReason.endsWith('missing-session-credentials')) {
      return 'missing-or-expired-token';
    }
    return normalizedReason;
  }

  function getStorefrontLoginPath() {
    return (window.shopifyContext && window.shopifyContext.storefrontLoginPath)
      ? window.shopifyContext.storefrontLoginPath
      : '/account/login';
  }

  function getCurrentReturnTo() {
    return `${window.location.pathname || '/'}${window.location.search || ''}`;
  }

  function hasStoredSessionCredentials() {
    return !!(Storage.get(CONFIG.STORAGE_KEYS.ACCESS_TOKEN) || Storage.get(CONFIG.STORAGE_KEYS.REFRESH_TOKEN));
  }

  function getShopifyRoot() {
    return (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
  }

  function cartUrl(path) {
    return getShopifyRoot() + path.replace(/^\//, '');
  }

  function buildCartEndpoints(path) {
    var primary = cartUrl(path);
    if (primary === path) {
      return [primary];
    }
    return [primary, path];
  }

  function buildCartUpdateEndpoints() {
    return buildCartEndpoints('/cart/update.js');
  }

  function postCartUpdate(payload) {
    var endpoints = buildCartUpdateEndpoints();

    function attempt(index) {
      if (index >= endpoints.length) {
        return Promise.resolve(null);
      }

      return fetch(endpoints[index], {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'same-origin',
        keepalive: true,
        body: JSON.stringify(payload)
      })
      .then(function(response) {
        if (!response || !response.ok) {
          return attempt(index + 1);
        }

        if (typeof response.json === 'function') {
          return response.json().catch(function() {
            return null;
          });
        }

        return null;
      })
      .catch(function() {
        return attempt(index + 1);
      });
    }

    return attempt(0);
  }

  function clearPromoCodeCartAttribute() {
    function hasPromoCode(cart) {
      return !!(cart && cart.attributes && cart.attributes.promo_code);
    }

    return postCartUpdate({ attributes: { promo_code: '' } })
      .then(function(cart) {
        if (!hasPromoCode(cart)) {
          return true;
        }

        // Some storefronts only clear cart attributes when sent as null.
        return postCartUpdate({ attributes: { promo_code: null } })
          .then(function(retryCart) {
            return !hasPromoCode(retryCart);
          });
      })
      .catch(function() {
        return false;
      });
  }

  function clearPromoCodeThen(navigate) {
    var done = false;
    var finish = function() {
      if (done) return;
      done = true;
      navigate();
    };

    clearPromoCodeCartAttribute().then(finish).catch(finish);
    setTimeout(finish, 350);
  }

  // Waits for the logout cart cleanup (reusing the in-flight request already
  // started by clearAuth()) so the page we land on after a full logout
  // navigation reflects an emptied cart. Falls back to navigating after a
  // timeout so logout is never blocked indefinitely.
  function clearLogoutStateThen(navigate) {
    var done = false;
    var finish = function() {
      if (done) return;
      done = true;
      navigate();
    };

    clearShopifyCartForLogout().then(finish).catch(finish);

    // Matches the native logout budget; the clear itself is keepalive, so a
    // timeout only means the landing page may briefly render a stale count.
    setTimeout(finish, 1200);
  }

  function scanProtectedPages() {
    if (window.location.pathname.includes('/pages/auth-callback')) {
      return;
    }

    if (!document || typeof document.querySelectorAll !== 'function') {
      return;
    }

    const protectedRoots = document.querySelectorAll('[data-requires-auth]');
    if (!protectedRoots.length) {
      return;
    }

    window.authState.ensureSessionOrRedirect('missing-token').catch(function() {
      window.authState.handleAuthFailure('missing-token');
    });
  }

  function redirectToLoginWithGuard(reason) {
    const path = window.location.pathname || '';
    const normalizedReason = normalizeAuthFailureReason(reason);

    // Avoid redirect loops while already in auth flow.
    if (path.includes('/pages/auth-callback')) {
      return false;
    }

    if (authRedirectInFlight) {
      return false;
    }

    authRedirectInFlight = true;
    if (authRedirectResetTimer) {
      clearTimeout(authRedirectResetTimer);
    }
    authRedirectResetTimer = setTimeout(() => {
      authRedirectInFlight = false;
      authRedirectResetTimer = null;
    }, 3000);

    try {
      window.dispatchEvent(new CustomEvent('authSessionExpired', {
        detail: {
          reason: normalizedReason,
          path: path,
          timestamp: Date.now()
        }
      }));
    } catch (e) {
      // Ignore telemetry event failures.
    }

    const loginPath = getStorefrontLoginPath();

    // Break potential login loops when Shopify still has an active session but app tokens are missing.
    if (normalizedReason === 'missing-or-expired-token') {
      const logoutAttemptKey = 'authState_logout_then_auth_attempted';
      const shopifyCustomerId = window.shopifyCustomer && window.shopifyCustomer.customerId;
      let hasLogoutAttempt = false;

      try {
        hasLogoutAttempt = sessionStorage.getItem(logoutAttemptKey) === '1';
      } catch (e) {}

      if (shopifyCustomerId && !hasLogoutAttempt) {
        try {
          sessionStorage.setItem(logoutAttemptKey, '1');
        } catch (e) {}

        const returnTo = getCurrentReturnTo();
        const loginWithReturn = `${loginPath}?return_to=${encodeURIComponent(returnTo)}`;
        const logoutUrl = `/account/logout?return_url=${encodeURIComponent(loginWithReturn)}`;
        clearPromoCodeThen(function() {
          window.location.replace(logoutUrl);
        });
        return true;
      }

      try {
        sessionStorage.removeItem(logoutAttemptKey);
      } catch (e) {}
    }

    if (typeof window.redirectToAuthInit === 'function') {
      window.redirectToAuthInit();
      return true;
    }

    window.location.href = loginPath;
    return true;
  }

  window.authState = {
    /**
     * Check if user is authenticated
     * Returns true if we have a valid (non-expired) token
     */
    isAuthenticated() {
      // Check token exists and is not expired (avoids double expiry check)
      return !!Storage.get(CONFIG.STORAGE_KEYS.ACCESS_TOKEN) && !TokenManager.isTokenExpired();
    },

    /**
     * Get current access token (async - auto-refreshes if expired)
     * @returns {Promise<string|null>} - Valid token or null
     */
    async getToken() {
      // Check if token is expired using JWT exp claim (more accurate)
      if (TokenManager.isTokenExpiredFromJWT()) {
        // Try to refresh if we have a refresh token
        if (this.getRefreshToken()) {
          // Debounce: reuse in-flight refresh request if one exists
          if (!refreshPromise) {
            refreshPromise = this.refreshToken().finally(() => {
              refreshPromise = null;
            });
          }
          const refreshed = await refreshPromise;
          if (refreshed) {
            return Storage.get(CONFIG.STORAGE_KEYS.ACCESS_TOKEN);
          }
        }
        return null; // No refresh token or refresh failed
      }

      return Storage.get(CONFIG.STORAGE_KEYS.ACCESS_TOKEN);
    },

    /**
     * Get current access token synchronously (no auto-refresh)
     * Use this when you can't await, but prefer getToken() when possible
     * @returns {string|null}
     */
    getTokenSync() {
      return TokenManager.getAccessToken();
    },

    /**
     * Returns true when any local session credential is present.
     * Useful for protected pages that need a synchronous early check.
     */
    hasSessionCredentials() {
      return !!this.getTokenSync() || hasStoredSessionCredentials();
    },

    /**
     * Get refresh token
     */
    getRefreshToken() {
      return TokenManager.getRefreshToken();
    },

    /**
     * Refresh the Firebase ID token using the refresh token
     * Calls Client API /auth/firebase-refresh endpoint
     * @returns {Promise<boolean>} - True if refresh successful
     */
    async refreshToken() {
      const refreshToken = this.getRefreshToken();

      if (!refreshToken) {
        return false;
      }

      const apiBaseUrl = window.API_BASE_URL;
      if (!apiBaseUrl) {
        console.error('[AuthState] API_BASE_URL not configured');
        return false;
      }

      try {
        const response = await fetch(`${apiBaseUrl}/auth/firebase-refresh`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ refreshToken })
        });

        if (!response.ok) {
          console.warn('[AuthState] Token refresh request failed:', response.status);
          window.dispatchEvent(new CustomEvent(CONFIG.EVENTS.TOKEN_REFRESH_FAILED));
          return false;
        }

        const data = await response.json();

        // Store new tokens from Client API response
        // Response format: { idToken, refreshToken, expiresIn, userId }
        if (data.idToken) {
          Storage.set(CONFIG.STORAGE_KEYS.ACCESS_TOKEN, data.idToken);
          Storage.set(CONFIG.STORAGE_KEYS.TOKEN_TIMESTAMP, Date.now().toString());
        }

        if (data.refreshToken) {
          Storage.set(CONFIG.STORAGE_KEYS.REFRESH_TOKEN, data.refreshToken);
        }

        window.dispatchEvent(new CustomEvent(CONFIG.EVENTS.TOKEN_REFRESHED));
        return true;
      } catch (error) {
        console.error('[AuthState] Token refresh failed:', error);
        window.dispatchEvent(new CustomEvent(CONFIG.EVENTS.TOKEN_REFRESH_FAILED));
        return false;
      }
    },

    /**
     * Check if token is expired
     */
    isExpired() {
      return TokenManager.isTokenExpired();
    },

    /**
     * Get user email from stored ID token
     * Parses directly from JWT for security (not stored separately)
     */
    getUserEmail() {
      try {
        const token = Storage.get(CONFIG.STORAGE_KEYS.ACCESS_TOKEN);
        if (token) {
          const claims = TokenManager._parseJWT(token);
          return claims.email || null;
        }
      } catch (e) {
        // Error parsing token
      }
      return null;
    },

    /**
     * Get user avatar URL from stored ID token
     */
    getUserAvatar() {
      return Storage.get(CONFIG.STORAGE_KEYS.USER_AVATAR);
    },

    /**
     * Get user name info
     */
    getUserName() {
      return {
        name: Storage.get(CONFIG.STORAGE_KEYS.USER_NAME),
        givenName: Storage.get(CONFIG.STORAGE_KEYS.USER_GIVEN_NAME),
        familyName: Storage.get(CONFIG.STORAGE_KEYS.USER_FAMILY_NAME)
      };
    },

    /**
     * Ensure user claims (email, avatar, name) are extracted from stored token
     * Call this on page load to ensure avatar is available
     * @returns {boolean} true if new claims were stored
     */
    ensureUserClaims() {
      // Check what we have before extraction
      const hadAvatar = !!Storage.get(CONFIG.STORAGE_KEYS.USER_AVATAR);
      const hadName = !!Storage.get(CONFIG.STORAGE_KEYS.USER_GIVEN_NAME);

      // Skip JWT parsing if claims are already stored (performance optimization)
      if (hadAvatar || hadName) {
        return false;
      }

      // Try to extract from stored access token
      const token = Storage.get(CONFIG.STORAGE_KEYS.ACCESS_TOKEN);
      if (token) {
        try {
          const claims = TokenManager._parseJWT(token);
          TokenManager._storeUserClaims(claims);
        } catch (e) {
          // Token is not a JWT or doesn't contain user claims
        }
      }

      // Check if we stored new avatar
      const hasAvatarNow = !!Storage.get(CONFIG.STORAGE_KEYS.USER_AVATAR);

      // Dispatch event if we stored new claims (avatar or name)
      // Use requestAnimationFrame to ensure custom elements are ready to receive the event
      if (!hadAvatar && hasAvatarNow) {
        requestAnimationFrame(() => {
          try {
            const event = new CustomEvent(CONFIG.EVENTS.AUTH_COMPLETE, {
              bubbles: true,
              cancelable: false,
              detail: {
                avatar: Storage.get(CONFIG.STORAGE_KEYS.USER_AVATAR),
                email: this.getUserEmail()
              }
            });
            window.dispatchEvent(event);
          } catch (e) {
            // Error dispatching event
          }
        });
        return true;
      }
      return false;
    },

    /**
     * Clear all authentication data.
     *
     * The Shopify cart is only emptied when the caller says this is a real
     * logout (`{ clearCart: true }`). This function is also reached from
     * ensureSessionOrRedirect() and handleAuthFailure(), i.e. an expired
     * Firebase token or a single 401 — a shopper who is still signed in to
     * Shopify and merely needs a token refresh must not lose their cart.
     *
     * @param {{clearCart?: boolean}} [options]
     */
    clearAuth(options) {
      const shouldClearCart = Boolean(options && options.clearCart === true);

      Storage.clearAuth();

      if (shouldClearCart) {
        // Optimistically remove stale cart count while logout cart clear is in flight.
        removeCartCountBubble();

        clearShopifyCartForLogout()
          .then(function() {
            return refreshCartIconBubbleAfterLogout();
          })
          .catch(function(error) {
            console.warn('[Auth] Failed to clear cart during clearAuth:', error);
          });
      }

      // Dispatch logout event
      try {
        const event = new CustomEvent(CONFIG.EVENTS.AUTH_LOGOUT, {
          bubbles: true,
          cancelable: false
        });
        window.dispatchEvent(event);
      } catch (e) {
        // Error dispatching logout event
      }

      return true;
    },

    /**
     * Ensures a protected page has a usable session, otherwise redirects.
     * Returns true when the current request may continue, false when redirecting.
     * @param {string} [reason]
     * @returns {Promise<boolean>}
     */
    async ensureSessionOrRedirect(reason) {
      const failureReason = normalizeAuthFailureReason(reason);

      if (this.getTokenSync()) {
        return true;
      }

      if (!hasStoredSessionCredentials()) {
        this.clearAuth();
        redirectToLoginWithGuard(failureReason);
        return false;
      }

      const token = await this.getToken();
      if (token) {
        return true;
      }

      this.handleAuthFailure(failureReason);
      return false;
    },

    /**
     * Clear invalid auth state and redirect to the auth flow.
     * @param {string} [reason]
     * @returns {boolean}
     */
    handleAuthFailure(reason) {
      this.clearAuth();
      return redirectToLoginWithGuard(normalizeAuthFailureReason(reason));
    },

    /**
     * Clear Shopify cart and client cart state as part of logout flow.
     * Returns a promise so callers can delay navigation until this settles.
     */
    clearCartForLogout() {
      return clearShopifyCartForLogout();
    },

    /**
     * Save redirect URL before login
     * @param {string} url
     * @returns {boolean}
     */
    saveRedirect(url) {
      return RedirectManager.saveRedirectURL(url);
    },

    /**
     * Get authentication state details
     */
    getState() {
      return {
        authenticated: this.isAuthenticated(),
        expired: this.isExpired(),
        email: this.getUserEmail(),
        shopifyCustomer: window.shopifyCustomer,
        timestamp: Storage.get(CONFIG.STORAGE_KEYS.TOKEN_TIMESTAMP)
      };
    },

    /**
     * Make an authenticated fetch request with automatic token refresh and 401 retry
     * @param {string} url - The URL to fetch
     * @param {RequestInit} [options={}] - Fetch options (method, body, headers, etc.)
     * @returns {Promise<Response>} - The fetch response
     * @throws {Error} - If not authenticated or refresh fails
     */
    async authenticatedFetch(url, options = {}) {
      // Get valid token (auto-refreshes if expired)
      let token = await this.getToken();

      if (!token) {
        this.handleAuthFailure('missing-or-expired-token');
        const missingAuthError = new Error('Not authenticated - redirecting to login');
        missingAuthError.code = 'AUTH_REQUIRED';
        throw missingAuthError;
      }

      // Merge auth header with existing headers
      const headers = {
        ...options.headers,
        'Authorization': `Bearer ${token}`
      };

      // Make the initial request
      let response = await fetch(url, { ...options, headers });

      // Handle 401 Unauthorized - try refresh once and retry
      if (response.status === 401) {
        // Attempt token refresh
        const refreshed = await this.refreshToken();

        if (refreshed) {
          // Get new token after refresh
          token = this.getTokenSync();

          if (token) {
            // Retry request with new token
            headers['Authorization'] = `Bearer ${token}`;
            response = await fetch(url, { ...options, headers });
          }
        }

        // If still 401 after refresh attempt, the refresh failed or token is invalid
        if (response.status === 401) {
          this.handleAuthFailure('unauthorized-after-refresh');
          const authFailedError = new Error('Authentication failed - session expired, redirecting to login');
          authFailedError.code = 'AUTH_SESSION_EXPIRED';
          throw authFailedError;
        }
      }

      return response;
    }
  };

  // Initialize if on callback page
  if (window.location.pathname.includes('/pages/auth-callback')) {
    // Wait for DOM to be ready
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', () => AuthHandler.init());
    } else {
      AuthHandler.init();
    }
  }

  /**
   * Global Logout Handler
   * Clears auth tokens when user clicks any logout link
   */
  function openNativeLogoutInAppBrowser(logoutUrl, returnPath, clickEvent) {
    const inAppBrowser = window.cordova && window.cordova.InAppBrowser;
    if (!inAppBrowser) {
      // Explicit fallback: without the Cordova plugin, keep the browser's
      // default navigation through the same authoritative logout URL.
      return false;
    }

    const expectedReturnUrl = new URL(returnPath, window.location.origin);
    const ref = inAppBrowser.open(
      logoutUrl.toString(),
      '_blank',
      'location=no,toolbar=yes'
    );
    let completed = false;

    // The IAB is now responsible for the complete Shopify -> external IdP ->
    // Shopify chain, so keep the main WebView on its current same-origin page.
    clickEvent.preventDefault();

    ref.addEventListener('loadstart', function(event) {
      if (completed) {
        return;
      }

      const loadedUrl = new URL(event.url, window.location.origin);
      const reachedStorefrontReturn = loadedUrl.origin === expectedReturnUrl.origin
        && loadedUrl.pathname === expectedReturnUrl.pathname;

      // The initial same-origin logout route deliberately does not match the
      // localized return path. External Shopify and IdP hops fail the exact
      // origin check, so only the completed storefront return closes the IAB.
      if (!reachedStorefrontReturn) {
        return;
      }

      completed = true;
      ref.close();
      window.location.href = loadedUrl.pathname + loadedUrl.search + loadedUrl.hash;
    });

    ref.addEventListener('exit', function() {
      if (!completed) {
        // Local tokens were cleared before opening the IAB. Reload the current
        // storefront page to reconcile UI state, without claiming that the
        // Shopify/IdP SSO chain completed.
        window.location.reload();
      }
    });

    return true;
  }

  function setupLogoutHandler() {
    document.addEventListener('click', function(e) {
      const link = e.target.closest('[data-web-logout-link]');
      if (link) {
        // Keep Shopify's Liquid-provided logout route authoritative while
        // refreshing its return URL from the runtime region contract. Locale
        // persistence can update shopifyContext after Liquid rendered the link.
        const context = window.shopifyContext || {};
        const logoutUrl = new URL(link.getAttribute('href'), window.location.origin);
        const renderedReturnUrl = logoutUrl.searchParams.get('return_url') || '/';
        const returnUrl = new URL(context.rootUrl || renderedReturnUrl, window.location.origin);

        if (!returnUrl.pathname.endsWith('/')) {
          returnUrl.pathname += '/';
        }
        if (context.region) {
          returnUrl.searchParams.set('country', context.region);
        }

        const returnPath = returnUrl.pathname + returnUrl.search;
        logoutUrl.searchParams.set('return_url', returnPath);
        const nextLogoutHref = logoutUrl.origin === window.location.origin
          ? logoutUrl.pathname + logoutUrl.search + logoutUrl.hash
          : logoutUrl.toString();
        link.setAttribute('href', nextLogoutHref);

        // Clear Firebase/auth tokens, then allow default navigation through the
        // complete Shopify logout chain in this WebView or browser tab.
        // This is a real logout, so the cart is emptied too (the native branch
        // below relies on this; the non-native branch also runs
        // clearLogoutStateThen()).
        if (window.authState && typeof window.authState.clearAuth === 'function') {
          window.authState.clearAuth({ clearCart: true });
        }

        var handledByNative = openNativeLogoutInAppBrowser(logoutUrl, returnPath, e);
        if (!handledByNative) {
          // Wait for cart + promo cleanup before leaving the current page so
          // the returned storefront cart state is immediately consistent
          // after logout. Timeout fallback preserves reliability.
          e.preventDefault();
          clearLogoutStateThen(function() {
            window.location.href = nextLogoutHref;
          });
        }
      }
    });
  }

  // Setup logout handler when DOM is ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', setupLogoutHandler);
  } else {
    setupLogoutHandler();
  }

  // Session sync: Clear auth tokens when Shopify session is no longer active.
  // This catches logouts initiated from Shopify's account page, checkout, or any
  // other path that bypasses the theme's [data-web-logout-link] handler.
  // Skip on auth-callback page where tokens are stored before the Shopify session exists.
  if (!window.location.pathname.includes('/pages/auth-callback')) {
    var guestPromoClearedKey = 'authState_guest_promo_code_cleared_v2';
    var accountLogoutCartClearedKey = 'authState_account_logout_cart_cleared_v1';
    var hasStoredTokens = Storage.get(CONFIG.STORAGE_KEYS.ACCESS_TOKEN);
    var shopifyLoggedIn = window.shopifyCustomer && window.shopifyCustomer.customerId;

    if (!shopifyLoggedIn) {
      if (hasStoredTokens) {
        // The Shopify session is gone while local tokens remain: the shopper
        // logged out somewhere this handler did not see (account page,
        // checkout, another tab). That is a real logout, so clear the cart.
        window.authState.clearAuth({ clearCart: true });
      }

      var promoAlreadyClearedForGuest = false;
      var isAccountLogoutReturn = false;
      try {
        promoAlreadyClearedForGuest = sessionStorage.getItem(guestPromoClearedKey) === '1';
      } catch (e) {}

      try {
        var referrer = String(document.referrer || '').toLowerCase();
        isAccountLogoutReturn = referrer.indexOf('account.shopify') !== -1
          || referrer.indexOf('/authentication/logout') !== -1
          || referrer.indexOf('/account/logout') !== -1;
      } catch (e) {}

      // Shopify's own account/logout page never runs our click handler, so a
      // returning guest here may still have a full cart even without stored
      // app tokens. clearAuth() above only clears the cart when local tokens
      // existed, so cover the token-less case explicitly (once per session).
      // The pending marker covers the same landing when the cross-domain logout
      // chain strips the referrer, or when navigation outran the clear.
      var accountLogoutCleanupPromise = null;
      var logoutClearPending = hasLogoutCartClearPending();
      if (logoutClearPending || (isAccountLogoutReturn && !hasStoredTokens)) {
        var accountLogoutCartAlreadyCleared = false;
        try {
          accountLogoutCartAlreadyCleared = sessionStorage.getItem(accountLogoutCartClearedKey) === '1';
        } catch (e) {}

        if (logoutClearPending || !accountLogoutCartAlreadyCleared) {
          accountLogoutCleanupPromise = clearShopifyCartForLogout().then(function() {
            try {
              sessionStorage.setItem(accountLogoutCartClearedKey, '1');
            } catch (e) {}
            setLogoutCartClearPending(false);
            resetDeferredLogoutClearAttempts();
            return refreshCartIconBubbleAfterLogout().then(function() {
              return true;
            });
          }).catch(function() {
            return false;
          }).finally(function() {
            releaseLogoutBadgeSuppression();
          });
        }
      }

      if (!accountLogoutCleanupPromise) {
        releaseLogoutBadgeSuppression();
      }

      if (!promoAlreadyClearedForGuest) {
        if (isAccountLogoutReturn) {
          window.__mimeoPromoCleanupFromAccountLogout = true;
        }

        // The full cleanup already clears promo_code as its first write; issuing
        // a second unqueued attribute update here could re-commit cleared lines.
        var promoCleanupPromise = accountLogoutCleanupPromise
          ? accountLogoutCleanupPromise
          : runSerializedCartMutation(clearPromoCodeCartAttribute);

        promoCleanupPromise = promoCleanupPromise.then(function(cleared) {
          if (cleared) {
            try {
              sessionStorage.setItem(guestPromoClearedKey, '1');
            } catch (e) {}
          }
          return cleared;
        });

        window.__mimeoPromoCleanupPromise = promoCleanupPromise.finally(function() {
          window.__mimeoPromoCleanupPromise = null;
          window.__mimeoPromoCleanupFromAccountLogout = false;
        });
      }
    } else {
      try {
        sessionStorage.removeItem(guestPromoClearedKey);
        sessionStorage.removeItem(accountLogoutCartClearedKey);
      } catch (e) {}

      // A pending marker while someone IS signed in means an earlier logout's
      // cart clear never completed (it failed, or navigation outran it) and a
      // new sign-in has happened since. Dropping the marker on its own handed
      // the new shopper the previous shopper's cart — the Shopify cart cookie
      // is per-browser, so on a shared device that is a real leak. Finish the
      // owed clear first, then drop the marker.
      if (hasLogoutCartClearPending() && !hasExhaustedDeferredLogoutClearAttempts()) {
        recordDeferredLogoutClearAttempt();
        clearShopifyCartForLogout()
          .then(function() {
            setLogoutCartClearPending(false);
            resetDeferredLogoutClearAttempts();
            return refreshCartIconBubbleAfterLogout();
          })
          .catch(function(error) {
            // Leave the marker set so the next page load retries, up to the
            // attempt cap above. Without a cap, a deployment where
            // /cart/clear.js is fronted by something answering 200 with a
            // non-JSON body would re-clear this shopper's cart on every single
            // page load.
            console.warn('[Auth] Deferred logout cart clear failed:', error);
          })
          .finally(function() {
            releaseLogoutBadgeSuppression();
          });
      } else {
        setLogoutCartClearPending(false);
        releaseLogoutBadgeSuppression();
      }

      window.__mimeoPromoCleanupPromise = null;
      window.__mimeoPromoCleanupFromAccountLogout = false;
    }
  }

  // Ensure user claims (avatar, name) are extracted from stored token on page load
  // We check for token existence, not authentication status, because we want to show
  // the avatar even if the token is expired (avatar is for display only)
  if (Storage.get(CONFIG.STORAGE_KEYS.ACCESS_TOKEN)) {
    window.authState.ensureUserClaims();
  }

  /**
   * Background Token Refresh Service
   * Proactively refreshes tokens before they expire to ensure seamless user experience
   */
  const TokenRefreshService = {
    intervalId: null,
    isRunning: false,
    boundHandlers: null,

    /**
     * Start the background refresh service
     */
    start() {
      if (this.isRunning) {
        return;
      }

      this.isRunning = true;

      // Store bound handlers to ensure proper cleanup later
      this.boundHandlers = {
        visibilityChange: this.handleVisibilityChange.bind(this),
        logout: () => this.stop()
      };

      // Initial check
      this.checkAndRefresh();

      // Set up periodic check
      this.intervalId = setInterval(() => {
        this.checkAndRefresh();
      }, CONFIG.TOKEN_CHECK_INTERVAL_MS);

      // Pause when tab is hidden, resume when visible
      document.addEventListener('visibilitychange', this.boundHandlers.visibilityChange);

      // Stop on logout
      window.addEventListener(CONFIG.EVENTS.AUTH_LOGOUT, this.boundHandlers.logout);

      console.log('[TokenRefreshService] Started background token refresh');
    },

    /**
     * Stop the background refresh service
     */
    stop() {
      if (this.intervalId) {
        clearInterval(this.intervalId);
        this.intervalId = null;
      }
      this.isRunning = false;

      // Remove event listeners using the same bound handlers
      if (this.boundHandlers) {
        document.removeEventListener('visibilitychange', this.boundHandlers.visibilityChange);
        window.removeEventListener(CONFIG.EVENTS.AUTH_LOGOUT, this.boundHandlers.logout);
        this.boundHandlers = null;
      }
      console.log('[TokenRefreshService] Stopped background token refresh');
    },

    /**
     * Handle visibility change - pause/resume refresh when tab is hidden/visible
     */
    handleVisibilityChange() {
      if (document.hidden) {
        // Tab is hidden - pause interval to save resources
        if (this.intervalId) {
          clearInterval(this.intervalId);
          this.intervalId = null;
        }
      } else {
        // Tab is visible again - check immediately and resume interval
        if (this.isRunning && !this.intervalId) {
          this.checkAndRefresh();
          this.intervalId = setInterval(() => {
            this.checkAndRefresh();
          }, CONFIG.TOKEN_CHECK_INTERVAL_MS);
        }
      }
    },

    /**
     * Check if token needs refresh and refresh if necessary
     */
    async checkAndRefresh() {
      // Only proceed if user has tokens (is logged in)
      const accessToken = Storage.get(CONFIG.STORAGE_KEYS.ACCESS_TOKEN);
      const refreshToken = Storage.get(CONFIG.STORAGE_KEYS.REFRESH_TOKEN);

      if (!accessToken || !refreshToken) {
        return;
      }

      // Check token expiry from JWT exp claim (more accurate than timestamp)
      const timeUntilExpiry = TokenManager.getTimeUntilExpiryFromJWT();
      const bufferTime = CONFIG.TOKEN_REFRESH_BUFFER_MINUTES * 60 * 1000;

      // If token will expire within buffer time, refresh proactively
      if (timeUntilExpiry <= bufferTime && timeUntilExpiry > 0) {
        console.log(`[TokenRefreshService] Token expires in ${Math.round(timeUntilExpiry / 1000 / 60)} minutes, refreshing proactively...`);
        try {
          const refreshed = await window.authState.refreshToken();
          if (refreshed) {
            console.log('[TokenRefreshService] Token refreshed successfully');
          } else {
            console.warn('[TokenRefreshService] Token refresh failed');
          }
        } catch (error) {
          console.error('[TokenRefreshService] Error during token refresh:', error);
        }
      } else if (timeUntilExpiry <= 0) {
        // Token already expired - try to refresh
        console.log('[TokenRefreshService] Token expired, attempting refresh...');
        try {
          const refreshed = await window.authState.refreshToken();
          if (!refreshed) {
            console.warn('[TokenRefreshService] Could not refresh expired token');
          }
        } catch (error) {
          console.error('[TokenRefreshService] Error refreshing expired token:', error);
        }
      }
    }
  };

  // Start background refresh service when DOM is ready (only if user is logged in)
  function initTokenRefreshService() {
    // Only start if user has tokens
    const hasTokens = Storage.get(CONFIG.STORAGE_KEYS.ACCESS_TOKEN) &&
                      Storage.get(CONFIG.STORAGE_KEYS.REFRESH_TOKEN);
    if (hasTokens) {
      TokenRefreshService.start();
    }

    // Also start service when user logs in (authenticationComplete event)
    window.addEventListener(CONFIG.EVENTS.AUTH_COMPLETE, () => {
      TokenRefreshService.start();
    });
  }

  // Initialize on DOM ready (skip on auth-callback page to avoid conflicts)
  if (!window.location.pathname.includes('/pages/auth-callback')) {
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', initTokenRefreshService);
    } else {
      initTokenRefreshService();
    }
  }

  // Expose TokenRefreshService for manual control if needed
  window.TokenRefreshService = TokenRefreshService;

  // Expose MimeoAuth to window for global access (alias for authState)
  window.MimeoAuth = window.authState;

  if (!window.location.pathname.includes('/pages/auth-callback')) {
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', scanProtectedPages);
    } else {
      scanProtectedPages();
    }
  }

  // Diagnostic logging is opt-in via ?auth_debug=true to avoid noisy/sensitive logs in production.
  const authDebugEnabled = new URLSearchParams(window.location.search).get('auth_debug') === 'true';
  if (authDebugEnabled && !window.location.pathname.includes('/pages/auth-callback')) {
    console.log('[AuthState] Page loaded. localStorage auth state:', {
      auth_token: !!Storage.get(CONFIG.STORAGE_KEYS.ACCESS_TOKEN),
      refresh_token: !!Storage.get(CONFIG.STORAGE_KEYS.REFRESH_TOKEN),
      auth_timestamp: Storage.get(CONFIG.STORAGE_KEYS.TOKEN_TIMESTAMP),
      total_keys: localStorage.length,
      origin: window.location.origin,
      path: window.location.pathname
    });
  }

})();
